import { Controller } from "@hotwired/stimulus"
import { debounce } from "../utils/turbo_helpers"

// Optimized wrestler search controller
export default class extends Controller {
  static targets = ["input", "results", "form", "loading"]
  static values = { 
    url: String,
    minLength: { type: Number, default: 2 },
    debounce: { type: Number, default: 300 }
  }

  connect() {
    this.previousQuery = ""
    this.abortController = null
    
    // Create debounced search function
    this.debouncedSearch = debounce(
      this.performSearch.bind(this), 
      this.debounceValue
    )
  }

  search(event) {
    const query = this.inputTarget.value.trim()
    
    // Show loading state if we have a loading target
    if (this.hasLoadingTarget) {
      this.loadingTarget.classList.toggle('hidden', query.length < this.minLengthValue)
    }
    
    // Don't search if query is too short or hasn't changed
    if (query.length < this.minLengthValue) {
      if (query.length === 0) {
        this.clear()
      }
      return
    }
    
    if (query === this.previousQuery) {
      return
    }
    
    // Cancel previous request if still pending
    if (this.abortController) {
      this.abortController.abort()
    }
    
    // Perform debounced search
    this.debouncedSearch(query)
  }

  async performSearch(query) {
    this.previousQuery = query
    
    // Create new abort controller for this request
    this.abortController = new AbortController()
    
    try {
      // Build URL with search parameters
      const url = new URL(this.urlValue, window.location.origin)
      url.searchParams.set('search', query)
      
      // Preserve existing filters if form exists
      if (this.hasFormTarget) {
        const formData = new FormData(this.formTarget)
        for (const [key, value] of formData.entries()) {
          if (key !== 'search' && value) {
            url.searchParams.set(key, value)
          }
        }
      }
      
      // Perform the search with abort signal
      const response = await fetch(url, {
        headers: {
          'Accept': 'text/html',
          'X-Requested-With': 'XMLHttpRequest'
        },
        signal: this.abortController.signal
      })
      
      if (!response.ok) {
        throw new Error(`Search failed: ${response.status}`)
      }
      
      const html = await response.text()
      this.updateResults(html)
      
    } catch (error) {
      // Ignore abort errors
      if (error.name !== 'AbortError') {
        console.error('Search failed:', error)
        this.showError()
      }
    } finally {
      // Hide loading state
      if (this.hasLoadingTarget) {
        this.loadingTarget.classList.add('hidden')
      }
      this.abortController = null
    }
  }

  updateResults(html) {
    // Parse response and update results efficiently
    const parser = new DOMParser()
    const doc = parser.parseFromString(html, 'text/html')
    
    // Update wrestler grid
    const newResults = doc.querySelector('.wrestler-grid')
    if (newResults && this.hasResultsTarget) {
      // Use innerHTML for better performance than replacing nodes
      this.resultsTarget.innerHTML = newResults.innerHTML
    }
    
    // Update result count if present
    const resultCount = doc.querySelector('.result-count')
    if (resultCount) {
      const currentCount = document.querySelector('.result-count')
      if (currentCount) {
        currentCount.innerHTML = resultCount.innerHTML
      }
    }
    
    // Trigger any lazy loading for new images
    if ('loading' in HTMLImageElement.prototype) {
      const images = this.resultsTarget.querySelectorAll('img[loading="lazy"]')
      images.forEach(img => {
        if (img.getBoundingClientRect().top < window.innerHeight * 2) {
          img.loading = 'eager'
        }
      })
    }
  }

  clear() {
    this.inputTarget.value = ""
    this.previousQuery = ""
    
    // Cancel any pending search
    if (this.abortController) {
      this.abortController.abort()
      this.abortController = null
    }
    
    // Hide loading state
    if (this.hasLoadingTarget) {
      this.loadingTarget.classList.add('hidden')
    }
    
    // Reset to show all wrestlers
    this.performSearch("")
  }

  showError() {
    if (this.hasResultsTarget) {
      this.resultsTarget.innerHTML = `
        <div class="text-center py-8 text-gray-500">
          <p>Search failed. Please try again.</p>
        </div>
      `
    }
  }

  handleKeydown(event) {
    // Clear search on Escape
    if (event.key === "Escape") {
      this.clear()
      this.inputTarget.blur()
    }
    
    // Trigger search on Enter
    if (event.key === "Enter") {
      event.preventDefault()
      const query = this.inputTarget.value.trim()
      if (query.length >= this.minLengthValue) {
        // Cancel any pending debounced search
        if (this.abortController) {
          this.abortController.abort()
        }
        // Trigger immediate search
        this.performSearch(query)
      }
    }
  }

  disconnect() {
    // Cancel any pending requests when controller disconnects
    if (this.abortController) {
      this.abortController.abort()
    }
  }
}